import { Contract, utils } from 'ethers';
import { shared } from '../Shared';

const tokenABI = [
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address,address) view returns (uint256)'
];

// Use the same terms as the transaction, not an earlier version's agreed terms
// when depositing without explicit approval of the current version.
export function depositTerms(agreement, action) {
  const party = agreement.youAreUser2 ? '2' : '1';
  if (action !== 'implicit_finalize' && agreement.agreedData) {
    const agreed = JSON.parse(agreement.agreedData);
    const token = agreed['amount_type' + party];
    const amountRaw = agreed['amount_raw' + party];
    return { token, amountRaw, amount: shared.divideDecimals(amountRaw, shared.decimalsForToken(token)),
      contractType: action === 'deposit' ? agreement.contractType :
        (agreed.amount_type1 === 'eth' && agreed.amount_type2 === 'eth' && agreed.arbitration_fee_type === 'eth' ? 'simple_eth' : 'simple_erc20') };
  }
  const token = agreement['user' + party + 'ContributionType'];
  const amount = agreement['user' + party + 'ContributionInput'];
  return { token, amount, amountRaw: shared.rawValueForToken(amount || '0', token), contractType: agreement.versionContractType };
}

export function depositFundsKey(auth, agreement, action) {
  return JSON.stringify([
    auth.status, auth.uuid, auth.username, auth.ethAddress, auth.ethNetwork, auth.testMode,
    agreement.agreementid, agreement.versionid, agreement.agreedData,
    agreement.contractType, agreement.versionContractType, agreement.youAreUser1, agreement.youAreUser2,
    agreement.user1ContributionType, agreement.user2ContributionType,
    agreement.user1ContributionInput, agreement.user2ContributionInput, action
  ]);
}

// Read-only RPC calls, through the connected wallet. Never request account
// access, a signature, an allowance approval or a transaction here.
export async function readDepositFunds(auth, terms, provider = window.ethersProvider) {
  if (!provider || !auth.ethAddress || auth.status !== 'loggedin') {
    throw new Error('Connect your wallet to check your balance.');
  }
  const expectedNetwork = auth.testMode ? '4' : '1';
  const checkWallet = async () => {
    const [accounts, chainId] = await Promise.all([provider.listAccounts(), provider.send('eth_chainId', [])]);
    if (!accounts[0] || accounts[0].toLowerCase() !== auth.ethAddress.toLowerCase()) {
      throw new Error('Your wallet account changed. Reconnect the account used for this agreement.');
    }
    if (utils.bigNumberify(chainId).toString() !== expectedNetwork || auth.ethNetwork !== expectedNetwork) {
      throw new Error('Switch your wallet to the network used by this agreement, then retry.');
    }
  };
  let timeout;
  try {
    return await Promise.race([
      (async () => {
        await checkWallet();
        const { token, amountRaw, contractType } = terms;
        if (!shared.CONTRACT_TYPE_LIST.includes(contractType) || !/^\d+$/.test(amountRaw)) {
          throw new Error('The deposit terms are unavailable. Reload the agreement and try again.');
        }
        let balancePromise;
        let allowancePromise;
        const info = token === 'eth' ? null : shared.getSupportedTokenList(auth.testMode).find(entry => entry.symbol === token);
        if (token !== 'eth' && !info) throw new Error('This agreement’s token is not supported.');
        const ethPromise = provider.getBalance(auth.ethAddress, 'latest');
        if (token === 'eth') {
          balancePromise = ethPromise;
          allowancePromise = Promise.resolve(amountRaw); // Native ETH needs no token allowance.
        } else {
          const contract = new Contract(info.contract, tokenABI, provider);
          balancePromise = contract.balanceOf(auth.ethAddress);
          allowancePromise = contract.allowance(auth.ethAddress, shared.getContractAddress(auth.testMode, contractType));
        }
        const [eth, balance, allowance] = await Promise.all([ethPromise, balancePromise, allowancePromise]);
        await checkWallet();
        const amount = utils.bigNumberify(amountRaw);
        const balanceRaw = utils.bigNumberify(balance).toString();
        return {
          balanceRaw,
          balanceDisplay: shared.divideDecimals(balanceRaw, shared.decimalsForToken(token)),
          hasBalance: utils.bigNumberify(balance).gte(amount),
          hasAllowance: utils.bigNumberify(allowance).gte(amount),
          // This checks that some ETH remains for gas, not the final gas cost.
          hasETH: utils.bigNumberify(eth).gt(token === 'eth' ? amount : 0)
        };
      })(),
      new Promise((resolve, reject) => {
        timeout = setTimeout(() => reject(new Error('The wallet balance check timed out. Please retry.')), 10000);
      })
    ]);
  } finally {
    clearTimeout(timeout);
  }
}
